import { useEffect } from "react";
import {
    Navigate,
    Outlet,
    Route,
    RouterProvider,
    ScrollRestoration,
    createBrowserRouter,
    createRoutesFromElements,
    useLocation,
    useNavigate,
} from "react-router-dom";

import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthContextProvider } from "@/context/auth/provider";
import { useAuth } from "@/hooks/useAuth";
import { EditFeedRecordRoute } from "@/routes/game-feed/EditFeedRecordRoute";
import GameReportFeedRoute from "@/routes/game-feed/GameFeedRoute";
import GameLayout from "@/routes/game/GameLayout";
import GameRoute from "@/routes/game/GameRoute";
import PlayersRoute from "@/routes/players/players";
import ReportsRoute from "@/routes/reports";
import RoundLayout from "@/routes/round/RoundLayout";
import RoundRoute from "@/routes/round/RoundRoute";
import CommentatorProfile from "@/routes/settings/commentator";
import EditStreamingChannelRoute from "@/routes/streaming-channels/edit-streaming-channel";
import StreamingChannelsRoute from "@/routes/streaming-channels/streaming-channels";
import TournamentSeriesRoute from "@/routes/tournament-series/TournamentSeriesRoute";
import TournamentStageLayout from "@/routes/tournament-stage/TournamentStageLayout";
import TournamentStageRoute from "@/routes/tournament-stage/TournamentStageRoute";
import TournamentLayout from "@/routes/tournament/TournamentLayout";
import TournamentsRoute from "@/routes/tournaments/tournaments";
import TrialLayout from "@/routes/trial/TrialLayout";
import TrialRoute from "@/routes/trial/TrialRoute";
import VideoRoute from "@/routes/video/video";
import { CreateVideoRoute } from "@/routes/videos/create-video";
import VideosRoute from "@/routes/videos/videos";

import AuthorizedRoute from "./components/AuthorizedRoute";
import AppLayout from "./components/layouts/AppLayout";
import { RouteMatch } from "./components/layouts/Breadbrumbs";
import ArticleDetailRoute from "./routes/articles/ArticleDetailRoute";
import ArticlesRoute from "./routes/articles/ArticlesRoute";
import EditArticleRoute from "./routes/articles/EditArticleRoute";
import NewArticleRoute from "./routes/articles/NewArticleRoute";
import { Login } from "./routes/auth/login";
import { Logout } from "./routes/auth/logout";
import GameLiveCoverageLayout from "./routes/game-live-coverage/GameLiveCoverageLayout";
import GameReportRoute from "./routes/game-live-coverage/GameReportRoute";
import GamesRoute from "./routes/games/games";
import LayoutDetailRoute from "./routes/layouts/LayoutDetailRoute";
import LayoutsRoute from "./routes/layouts/LayoutsRoute";
import { UploadGfxRoute } from "./routes/studio-gfx";
import LayoutTemplateDetailRoute from "./routes/tournament-layout-templates/LayoutTemplateDetailRoute";
import LayoutTemplatesRoute from "./routes/tournament-layout-templates/LayoutTemplatesRoute";
import EditTournamentSeriesRoute from "./routes/tournament-series/EditTournamentSeriesRoute";
import NewTournamentSeriesRoute from "./routes/tournament-series/NewTournamentSeriesRoute";
import TournamentSeriesDetailRoute from "./routes/tournament-series/TournamentSeriesDetailRoute";
import TournamentLayoutDetailRoute from "./routes/tournament/TournamentLayoutDetailRoute";
import TournamentLayoutsRoute from "./routes/tournament/TournamentLayoutsRoute";
import TournamentRoute from "./routes/tournament/TournamentRoute";
import TroubleshootingRoute from "./routes/troubleshooting/troubleshooting";
import { LanguageRedirect } from "./utils/language/LanguageRedirect";

const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            <Route path="/auth/login" element={<Login />} />
            <Route element={<RootRoute />}>
                <Route element={<AuthorizedRoute />}>
                    <Route path="studio/gfx" element={<UploadGfxRoute />} />
                    <Route element={<AppLayout />}>
                        <Route
                            path="games?"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Games",
                                }),
                            }}
                        >
                            <Route index element={<GamesRoute />} />
                            <Route
                                path=":gameId"
                                element={<GameLiveCoverageLayout />}
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "Game live coverage",
                                    }),
                                }}
                            >
                                <Route index element={<Navigate to="reports" />} />

                                <Route path="reports" element={<ReportsRoute />}>
                                    <Route
                                        index
                                        element={<LanguageRedirect nextUrlPattern={lang => lang} />}
                                        handle={{
                                            crumb: ({ pathname }: RouteMatch) => ({
                                                path: pathname,
                                                element: "Reports",
                                            }),
                                        }}
                                    />
                                    <Route
                                        path="feed"
                                        element={
                                            <LanguageRedirect
                                                nextUrlPattern={lang => `../${lang}/feed`}
                                            />
                                        }
                                    />

                                    <Route path=":languageCode">
                                        <Route index element={<GameReportRoute />} />
                                        <Route path="feed" element={<GameReportFeedRoute />}>
                                            <Route
                                                path={"edit/:feedRecordId"}
                                                element={<EditFeedRecordRoute />}
                                            />
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>
                        </Route>
                        <Route
                            path="home-layouts"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Home layouts",
                                }),
                            }}
                        >
                            <Route index element={<LayoutsRoute />} />
                            <Route
                                path=":id"
                                element={<LayoutDetailRoute />}
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "Edit layout",
                                    }),
                                }}
                            />
                        </Route>
                        <Route
                            path="tournament-layout-templates"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Tournament Layout Templates",
                                }),
                            }}
                        >
                            <Route index element={<LayoutTemplatesRoute />} />
                            <Route
                                path=":templateId"
                                element={<LayoutTemplateDetailRoute />}
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "Edit template",
                                    }),
                                }}
                            />
                        </Route>
                        <Route
                            path="articles"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Articles",
                                }),
                            }}
                        >
                            <Route index element={<ArticlesRoute />} />
                            <Route
                                path="new"
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "New",
                                    }),
                                }}
                                element={<NewArticleRoute />}
                            />
                            <Route
                                path=":id"
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "View",
                                    }),
                                }}
                                element={<ArticleDetailRoute />}
                            />
                            <Route
                                path=":id/edit"
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "Edit",
                                    }),
                                }}
                                element={<EditArticleRoute />}
                            />
                        </Route>
                        <Route
                            path="streaming-channels"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Streaming Channels",
                                }),
                            }}
                        >
                            <Route index element={<StreamingChannelsRoute />} />
                            <Route
                                path=":id/edit"
                                element={<EditStreamingChannelRoute />}
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "Edit",
                                    }),
                                }}
                            />
                        </Route>
                        <Route
                            path="tournament-series"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Tournament Series",
                                }),
                            }}
                        >
                            <Route index element={<TournamentSeriesRoute />} />
                            <Route
                                path="new"
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "New",
                                    }),
                                }}
                                element={<NewTournamentSeriesRoute />}
                            />
                            <Route
                                path=":id/edit"
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "Edit",
                                    }),
                                }}
                                element={<EditTournamentSeriesRoute />}
                            />
                            <Route
                                path=":id"
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "View",
                                    }),
                                }}
                                element={<TournamentSeriesDetailRoute />}
                            />
                        </Route>
                        <Route
                            path="tournaments"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Tournaments",
                                }),
                            }}
                        >
                            <Route index element={<TournamentsRoute />} />
                            <Route
                                path=":tournamentId"
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "Tournament",
                                    }),
                                }}
                                element={<TournamentLayout />}
                            >
                                <Route index element={<TournamentRoute />} />
                                <Route
                                    path="layouts"
                                    handle={{
                                        crumb: ({ pathname }: RouteMatch) => ({
                                            path: pathname,
                                            element: "Layouts",
                                        }),
                                    }}
                                >
                                    <Route index element={<TournamentLayoutsRoute />} />
                                    <Route
                                        path=":layoutId"
                                        element={<TournamentLayoutDetailRoute />}
                                        handle={{
                                            crumb: ({ pathname }: RouteMatch) => ({
                                                path: pathname,
                                                element: "Edit Layout",
                                            }),
                                        }}
                                    />
                                </Route>
                                <Route path="stages">
                                    <Route
                                        path=":tournamentStageId"
                                        element={<TournamentStageLayout />}
                                        handle={{
                                            crumb: ({ pathname }: RouteMatch) => ({
                                                path: pathname,
                                                element: "Stage",
                                            }),
                                        }}
                                    >
                                        <Route index element={<TournamentStageRoute />} />
                                        <Route path="rounds">
                                            <Route
                                                path=":roundId"
                                                element={<RoundLayout />}
                                                handle={{
                                                    crumb: ({ pathname }: RouteMatch) => ({
                                                        path: pathname,
                                                        element: "Round",
                                                    }),
                                                }}
                                            >
                                                <Route index element={<RoundRoute />} />
                                                <Route path="trials">
                                                    <Route
                                                        path=":trialId"
                                                        element={<TrialLayout />}
                                                        handle={{
                                                            crumb: ({ pathname }: RouteMatch) => ({
                                                                path: pathname,
                                                                element: "Trial",
                                                            }),
                                                        }}
                                                    >
                                                        <Route index element={<TrialRoute />} />
                                                        <Route path="games">
                                                            <Route
                                                                path=":gameId"
                                                                element={<GameLayout />}
                                                                handle={{
                                                                    crumb: ({
                                                                        pathname,
                                                                    }: RouteMatch) => ({
                                                                        path: pathname,
                                                                        element: "Game",
                                                                    }),
                                                                }}
                                                            >
                                                                <Route
                                                                    index
                                                                    element={<GameRoute />}
                                                                />
                                                            </Route>
                                                        </Route>
                                                    </Route>
                                                </Route>
                                            </Route>
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>
                        </Route>
                        <Route path="players" element={<PlayersRoute />} />
                        <Route path="players/:id" element={<PlayersRoute />} />
                        <Route
                            path="videos?"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Videos",
                                }),
                            }}
                        >
                            <Route index element={<VideosRoute />} />
                            <Route path={"create"} element={<CreateVideoRoute />} />
                            <Route
                                path=":videoId"
                                element={<VideoRoute />}
                                handle={{
                                    crumb: ({ pathname }: RouteMatch) => ({
                                        path: pathname,
                                        element: "Video",
                                    }),
                                }}
                            />
                        </Route>
                        <Route
                            path="settings"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Settings",
                                }),
                            }}
                        >
                            <Route index element={<Navigate to="commentator" replace />} />
                            <Route path="commentator" element={<CommentatorProfile />} />
                        </Route>
                        <Route
                            path="troubleshooting"
                            handle={{
                                crumb: ({ pathname }: RouteMatch) => ({
                                    path: pathname,
                                    element: "Troubleshooting",
                                }),
                            }}
                            element={<TroubleshootingRoute />}
                        />
                        <Route path="logout" element={<Logout />} />
                    </Route>
                </Route>
            </Route>
        </>
    )
);

function RootRoute() {
    const { authUser, authUserLoading } = useAuth();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    useEffect(() => {
        if (authUserLoading) {
            return;
        }
    }, [authUser, authUserLoading, navigate, pathname, search]);

    if (authUserLoading) {
        return <div className="flex h-screen items-center justify-center"></div>;
    }

    if (!authUser) {
        return <Navigate to={`/auth/login?next=${encodeURIComponent(pathname)}`} />;
    }

    return (
        <>
            <Outlet />
            <ScrollRestoration />
        </>
    );
}

function App() {
    return (
        <TooltipProvider>
            <AuthContextProvider>
                <RouterProvider router={router} />
            </AuthContextProvider>
            <Toaster richColors />
        </TooltipProvider>
    );
}

export default App;
